import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import constant from "config/constant";
import { wrapBaseApi } from "util/apiWrapper";
import ls from "util/secureStorage";

// console.log("AUTH HEADER", _headers);

// Define a service using a base URL and expected endpoints
export const cardsApi = createApi({
  reducerPath: "cards",
  // refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  tagTypes: ["userCards", "userCardDetail", "userCardTransactions", "userCardProfile"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_WALLET_API_ENDPOINT}`,
    prepareHeaders: (headers, _) => {
      const auth = ls.get(constant.AUTH_TOKEN_KEY);

      headers.set("Authorization", auth.token);
      headers.set("Content-Type", "application/json");
      headers.set("Cache-Control", "no-cache, no-store, must-revalidate");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getFirstRequestCheck: builder.query({
      // query: () => `/wallet/card`,
      query: () => wrapBaseApi(`/wallet/card/first-time-check`),
      transformResponse: (res) => res?.data,
    }),
    getLGAs: builder.query({
      // query: () => `/wallet/card`,
      query: (state) => wrapBaseApi(`/countries/nigeria/${state}/lgas`),
      transformResponse: (res) => res?.data,
    }),

    getPreorderStatus: builder.query({
      // query: () => `/wallet/card`,
      query: () => wrapBaseApi(`/wallet/card/p/requests`),
      transformResponse: (res) => res,
    }),

    getPreorderFee: builder.query({
      query: () => wrapBaseApi(`/wallet/card/p/fee`),
      transformResponse: (res) => res?.data,
    }),
    getUserCards: builder.query({
      // query: () => wrapBaseApi(`/wallet/card`),
      query: (queries) => wrapBaseApi(`/wallet/card/card-from-db?${queries ?? ""}`),
      transformResponse: (res) => res,
      providesTags: ["userCards"],
    }),
    getUserCardProfile: builder.query({
      query: () => wrapBaseApi(`/wallet/card/profile`),
      transformResponse: (res) => res?.data,
      providesTags: ["userCardProfile"],
    }),
    getUserCardDetail: builder.mutation({
      // query: () => wrapBaseApi(`/wallet/card`),
      query: ({ id, pin, queries }) => ({
        url: wrapBaseApi(`/wallet/card/card-detail/${id}?${queries ?? ""}`),
        method: "POST",
        body: { pin },
      }),
      transformResponse: (res) => res,
      providesTags: ["userCardDetail"],
    }),
    getUserCardTransactions: builder.query({
      // query: (serviceId) =>
      //   wrapBaseApi(`/wallet/card/card-transactions?cardServiceId=${serviceId}`),
      query: (queries) => wrapBaseApi(`/wallet/card/card-transactions?${queries}`),
      transformResponse: (res) => res,
      providesTags: ["userCardTransactions"],
    }),
    getCardLimits: builder.query({
      query: () => wrapBaseApi(`/wallet/card/card-types`),
      transformResponse: (res) => res?.data,
    }),
    createCard: builder.mutation({
      query: (input) => {
        return {
          url: wrapBaseApi(`/wallet/card`),
          method: "POST",
          body: input,
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["userCards"],
    }),
    preorderPhysicalCard: builder.mutation({
      query: (input) => {
        return {
          url: wrapBaseApi(`/wallet/card/p/preorder`),
          method: "POST",
          body: input,
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["userCards"],
    }),
    fundCard: builder.mutation({
      query: (input) => {
        return {
          url: wrapBaseApi(`/wallet/card/fund/${input.id}`),
          method: "POST",
          body: input,
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["userCardDetail", "userCardTransactions"],
    }),
    freezeUserCard: builder.mutation({
      query: (id) => {
        return {
          url: wrapBaseApi(`/wallet/card/freeze-card/${id}`),
          method: "PUT",
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["userCardDetail", "userCards"],
    }),
    unfreezeUserCard: builder.mutation({
      query: (id) => {
        return {
          url: wrapBaseApi(`/wallet/card/unfreeze-card/${id}`),
          method: "PUT",
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["userCardDetail", "userCards"],
    }),
    withdrawFromCard: builder.mutation({
      query: ({ id, provider, ...input }) => {
        return {
          url: wrapBaseApi(`/wallet/card/withdraw/${id}${provider ? `?p=${provider}` : ""}`),
          method: "POST",
          body: { ...input },
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["userCardDetail", "userCards"],
    }),
    liquidateCard: builder.mutation({
      query: ({ id }) => {
        return {
          url: wrapBaseApi(`/wallet/card/liquidate/${id}`),
          method: "POST",
          body: {},
        };
      },
      transformResponse: (res) => res.data,
      invalidatesTags: ["userCardDetail", "userCards"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints...

export const {
  useGetLGAsQuery,
  useGetUserCardProfileQuery,
  useGetPreorderFeeQuery,
  useGetPreorderStatusQuery,
  useGetUserCardsQuery,
  // useGetUserCardDetailQuery,
  useGetUserCardDetailMutation,
  useGetUserCardTransactionsQuery,
  useGetCardLimitsQuery,
  useGetFirstRequestCheckQuery,
  useCreateCardMutation,
  usePreorderPhysicalCardMutation,
  useFundCardMutation,
  useFreezeUserCardMutation,
  useUnfreezeUserCardMutation,
  useWithdrawFromCardMutation,
  useLiquidateCardMutation,
} = cardsApi;

export default cardsApi;
